import { computed, ref, onMounted, onUnmounted } from 'vue';

export const Subscription = {
  PRO: 0,
  PLUS: 1,
  BASIC: 2
}

export const useAuth = () => {
  // Access the injected PocketBase instance
  const { $pb } = useNuxtApp();
  
  // Create a ref to store the latest user data
  const userData = ref($pb.authStore.model);
  
  // Computed properties based on userData instead of directly from authStore
  const isAuthenticated = computed(() => $pb.authStore.isValid);
  const user = computed(() => userData.value);
  const subscriptionType = computed(() => {
    // If the user or subscription field is missing, default to BASIC
    if (!user.value || !user.value.subscription) {
      return Subscription.BASIC;
    }
    
    // Use toLowerCase to handle possible case differences
    switch (user.value.subscription.toLowerCase()) {
      case 'plus':
        return Subscription.PLUS;
      case 'pro':
        return Subscription.PRO;
      default:
        return Subscription.BASIC;
    }
  });
  
  // Store the subscription for cleanup
  let subscription = null;
  
  // Simple function to refresh user data
  const refreshUserData = async () => {
    if ($pb.authStore.isValid && $pb.authStore.model?.id) {
      try {
        const freshData = await $pb.collection(COLLECTIONS.USERS).getOne($pb.authStore.model.id);
        userData.value = freshData;
      } catch (error) {
        console.error("Failed to refresh user data:", error);
      }
    }
  };
  
  // Handle auth changes and set up subscription
  onMounted(() => {
    // Initial fetch if user is already authenticated
    if ($pb.authStore.isValid) {
      refreshUserData();
      
      // Set up subscription for the current user
      if ($pb.authStore.model?.id) {
        subscription = $pb.collection(COLLECTIONS.USERS).subscribe($pb.authStore.model.id, (data) => {
          userData.value = data.record;
        });
      }
    }
    
    // Listen for auth changes
    $pb.authStore.onChange(() => {
      // Update user data
      userData.value = $pb.authStore.model;
      
      // Set up new subscription if authenticated
      if ($pb.authStore.isValid && $pb.authStore.model?.id) {
        refreshUserData();
        subscription = $pb.collection(COLLECTIONS.USERS).subscribe($pb.authStore.model.id, (data) => {
          userData.value = data.record;
        });
      }
    });
  });
  
  // Clean up on component unmount
  onUnmounted(() => {
    if (subscription) {
      subscription = null;
    }
  });

  /**
   * Login with email and password
   */
  const loginWithEmail = async (email, password) => {
    try {
      const authData = await $pb.collection(COLLECTIONS.USERS).authWithPassword(email, password);
      
      // Check if the user's email is verified
      if (!authData.record.verified) {
        $pb.authStore.clear(); // Log out since email is not verified
        throw new Error('Email not verified. Please check your inbox for the verification link.');
      }
      
      return authData;
    } catch (error) {
      console.error("loginWithEmail failed:", error);
      throw error;
    }
  };

  /**
   * Login with Google OAuth2
   */
  const loginWithGoogle = async () => {
    if (!import.meta.client) {
      throw new Error('OAuth can only be performed on the client side');
    }
    try {
      const authData = await $pb.collection(COLLECTIONS.USERS).authWithOAuth2({
        provider: 'google',
      });
      return authData;
    } catch (error) {
      console.error("loginWithGoogle failed:", error);
      throw error;
    }
  };

  /**
   * Register a new user with email
   */
  const registerWithEmail = async (email, password, name) => {
    try {
      const userData = {
        email,
        password,
        passwordConfirm: password,
        name,
        verified: false // Initial state, will be updated after verification
      };
      
      // Create the user
      const record = await $pb.collection(COLLECTIONS.USERS).create(userData);
      
      // Send verification email
      await $pb.collection(COLLECTIONS.USERS).requestVerification(email);
      
      return record;
    } catch (error) {
      console.error("registerWithEmail failed:", error);
      throw error;
    }
  };

  /**
   * Verify a user's email with the token received via email
   */
  const verifyEmail = async (token) => {
    try {
      return await $pb.collection(COLLECTIONS.USERS).confirmVerification(token);
    } catch (error) {
      console.error("verifyEmail failed:", error);
      throw error;
    }
  };

  /**
   * Send a password reset email
   */
  const sendPasswordResetEmail = async (email) => {
    try {
      return await $pb.collection(COLLECTIONS.USERS).requestPasswordReset(email);
    } catch (error) {
      console.error("sendPasswordResetEmail failed:", error);
      throw error;
    }
  };

  /**
   * Reset password using the token received via email
   */
  const resetPassword = async (token, newPassword) => {
    try {
      return await $pb.collection(COLLECTIONS.USERS).confirmPasswordReset(
        token,
        newPassword,
        newPassword // PocketBase requires confirmation
      );
    } catch (error) {
      console.error("resetPassword failed:", error);
      throw error;
    }
  };

  /**
   * Resend verification email
   */
  const resendVerificationEmail = async (email) => {
    try {
      return await $pb.collection(COLLECTIONS.USERS).requestVerification(email);
    } catch (error) {
      console.error("resendVerificationEmail failed:", error);
      throw error;
    }
  };

  /**
   * Logout the current user
   */
  const logout = () => {
    if (subscription) {
      subscription = null;
    }
    $pb.authStore.clear();
    userData.value = null;
  };

  // Simple method to manually refresh user data if needed
  const refreshUser = () => refreshUserData();

  return {
    isAuthenticated,
    user,
    subscriptionType,
    loginWithEmail,
    loginWithGoogle,
    registerWithEmail,
    verifyEmail,
    sendPasswordResetEmail,
    resetPassword,
    resendVerificationEmail,
    logout,
    refreshUser,
  };
};